import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { classBackupApi } from "./api";
import { PreviewResponse, WEEKDAY_LABELS } from "./types";

// Read-only detail view of what the import WILL do for one legacy staff.
// This calls /preview which itself makes no writes — safe to open anytime.
type Props = { legacyStaffId: number; cutoff: string; onClose: () => void };

const actionColor: Record<string, any> = {
  create: "success", update: "info", skip: "default", blocked: "error",
};

export default function PreviewDrawer({ legacyStaffId, cutoff, onClose }: Props) {
  const [data, setData] = useState<PreviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true); setError(null);
    classBackupApi.preview(legacyStaffId, cutoff)
      .then(setData)
      .catch((e) => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  }, [legacyStaffId, cutoff]);

  return (
    <Drawer anchor="right" open onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", md: 720 } } }}>
      <Box p={2}>
        <Stack direction="row" alignItems="center" mb={1}>
          <Typography variant="h6" flex={1}>Preview — legacy staff {legacyStaffId}</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        {loading && <Box textAlign="center" py={4}><CircularProgress /></Box>}
        {error && <Alert severity="error">{error}</Alert>}

        {data && (
          <>
            <Stack spacing={0.5} mb={2}>
              <Typography>
                <b>Legacy:</b> {data.staff_name || "—"} (id {data.legacy_staff_id})
              </Typography>
              <Typography>
                <b>Live:</b> {data.live_staff_name || "—"} {data.live_staff_id && `(id ${data.live_staff_id})`}
              </Typography>
              <Typography>
                <b>Cutoff:</b> {data.cutoff_date || "—"} &nbsp;•&nbsp;
                <b>Classes:</b> {data.groups_count} &nbsp;•&nbsp;
                <b>Active students:</b> {data.active_students} &nbsp;•&nbsp;
                <b>Future sessions:</b> {data.future_sessions}
              </Typography>
              <Chip size="small"
                color={data.status === "ok" ? "success" : data.status === "partial" ? "warning" : "error"}
                label={`status: ${data.status}`}
                sx={{ alignSelf: "flex-start" }} />
              {data.blocking_reasons?.length ? (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {data.blocking_reasons.map((r, i) => <div key={i}>{r}</div>)}
                </Alert>
              ) : null}
            </Stack>

            {data.classes.map((c) => (
              <Box key={c.legacy_slot} mb={3} p={1.5} border="1px solid #eee" borderRadius={1}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1} flexWrap="wrap">
                  <Chip size="small" color={actionColor[c.action]} label={c.action.toUpperCase()} />
                  <Typography fontWeight={600}>
                    {c.from_time}–{c.to_time}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {c.days.map((d) => WEEKDAY_LABELS[d]).join(", ")}
                  </Typography>
                  <Box flex={1} />
                  <Typography variant="caption" color="text.secondary">
                    slot: {c.legacy_slot}{c.existing_class_id ? ` · live #${c.existing_class_id}` : ""}
                  </Typography>
                </Stack>

                {/* §9 card summary — human-first, single line. Includes the
                    days as words (Monday · 4:00 PM–5:00 PM · In Person · <loc>). */}
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  {c.days.map((d) => WEEKDAY_LABELS[d]).join(" · ")} · {c.from_time}–{c.to_time}
                  {" · "}{c.class_mode}
                  {" · "}
                  {c.location_name
                    ? c.location_name
                    : <b style={{ color: "#b58a00" }}>Location: Not assigned</b>}
                </Typography>
                <Typography variant="body2" mb={1}>
                  <b>Students:</b> {c.active_roster_count}
                  {" · "}<b>Capacity:</b> {c.capacity}
                  {c.legacy_capacity !== undefined && c.legacy_capacity !== c.capacity && (
                    <Typography component="span" variant="caption" color="warning.main">
                      {" "}(raised from {c.legacy_capacity})
                    </Typography>
                  )}
                  {" · "}<b>Sessions:</b> {c.session_count_source}
                </Typography>
                {c.warnings && c.warnings.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {c.warnings.map((w, i) => <div key={i}>{w}</div>)}
                  </Alert>
                )}
                {c.location_conflicts.length > 0 && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    <b>Location conflict — resolve manually before importing:</b>
                    {c.location_conflicts.map((lc, i) => (
                      <div key={i}>
                        {lc.source === "per_student"
                          ? `Student disagreement: live ${lc.live_location} vs legacy Study Mode ${lc.legacy_location} (mode ${lc.legacy_study_mode ?? "?"})`
                          : `${lc.location_name ?? `id ${lc.location_id}`}: ${lc.student_count} student(s)`}
                      </div>
                    ))}
                  </Alert>
                )}

                <Accordion disableGutters elevation={0} sx={{ mb: 1, "&:before": { display: "none" } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1, minHeight: 32 }}>
                    <Typography variant="caption" color="text.secondary">View technical details</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 1, pt: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                      Grade/subject tokens: {c.subject_tokens.join(", ") || "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Legacy slot: {c.legacy_slot}
                      {c.existing_class_id ? ` · matches live class #${c.existing_class_id}` : ""}
                      {c.ambiguous_match && c.ambiguous_match_ids && (
                        <> · <b style={{ color: "#c62828" }}>Ambiguous — matches live #{c.ambiguous_match_ids.join(", #")}</b></>
                      )}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
                {c.missing_subjects.length > 0 && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    Missing underlying live grade or subject: {c.missing_subjects.map((m) => m.label).join(", ")}
                  </Alert>
                )}

                {c.blocking_reasons.length > 0 && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {c.blocking_reasons.map((r, i) => <div key={i}>{r}</div>)}
                  </Alert>
                )}

                <Typography variant="subtitle2" mt={1}>Students to enrol</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Legacy #</TableCell>
                      <TableCell>Live #</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Subjects</TableCell>
                      <TableCell align="right">Sessions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {c.students.map((s) => (
                      <TableRow key={s.legacy_student_id}>
                        <TableCell>{s.legacy_student_id}</TableCell>
                        <TableCell>{s.live_student_id}</TableCell>
                        <TableCell>{s.name || "—"}</TableCell>
                        <TableCell>{s.subject_ids}</TableCell>
                        <TableCell align="right">{s.active_dates_count}</TableCell>
                      </TableRow>
                    ))}
                    {c.students.length === 0 && (
                      <TableRow><TableCell colSpan={5} align="center">No active students.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>

                {(c.unmapped_students.length > 0 || c.dropped_students.length > 0 ||
                  c.excluded_counts.released + c.excluded_counts.cancelled + c.excluded_counts.makeup > 0) && (
                  <Box mt={1}>
                    <Typography variant="subtitle2">Excluded students</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Released: {c.excluded_counts.released} ·
                      Cancelled: {c.excluded_counts.cancelled} ·
                      Makeup: {c.excluded_counts.makeup} ·
                      Dropped: {c.excluded_counts.dropped} ·
                      Unmapped: {c.unmapped_students.length}
                    </Typography>
                    {c.dropped_students.length > 0 && (
                      <Box mt={0.5}>
                        {c.dropped_students.map((d) => (
                          <Typography key={d.legacy_student_id} variant="caption" color="text.secondary" display="block">
                            • legacy #{d.legacy_student_id}{d.live_student_id ? ` / live #${d.live_student_id}` : ""} — {d.name || "—"} · {d.reason}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                {c.existing_class_id && (c.diff.to_add.length > 0 || c.diff.to_soft_remove.length > 0) && (
                  <Box mt={1}>
                    <Typography variant="subtitle2">Diff vs. existing</Typography>
                    {c.diff.to_add.length > 0 && (
                      <Typography variant="body2" color="success.main">
                        Add: {c.diff.to_add.join(", ")}
                      </Typography>
                    )}
                    {c.diff.to_soft_remove.length > 0 && (
                      <Typography variant="body2" color="warning.main">
                        Soft-remove from future sessions: {c.diff.to_soft_remove.join(", ")}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            ))}
          </>
        )}
      </Box>
    </Drawer>
  );
}
